// Copyright (C) 2019-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import Select from 'antd/lib/select';
import { SelectValue } from 'antd/lib/select';

import { getCore } from 'cvat-core-wrapper';

const core = getCore();

type Props = {
    projectId: number | null;
    value: number[] | null; // Array to hold multiple task IDs
    onSelect: (ids: number[] | null) => void; // Handle multiple task IDs
    filter?: (value: Task, index: number, array: Task[]) => unknown;
};

type Task = {
    id: number;
    name: string;
};

export default function TaskSearchField(props: Props): JSX.Element {
    const { value, filter, onSelect, projectId } = props;
    const [searchPhrase, setSearchPhrase] = useState('');

    const [tasks, setTasks] = useState<Task[]>([]);

    const handleSearch = (searchValue: string): void => {
        core.tasks.searchNames(searchValue, 10, projectId).then((result: Task[]) => {
            if (result) {
                setTasks(result);
            }
        });
        setSearchPhrase(searchValue);
        onSelect([]); // Clear selected tasks when searching
    };

    const handleFocus = (open: boolean): void => {
        if (!tasks.length && open) {
            core.tasks.searchNames(null, 10, projectId).then((result: Task[]) => {
                let tasksResponse = result;
                if (typeof filter === 'function') {
                    tasksResponse = tasksResponse.filter(filter);
                }
                if (tasksResponse) {
                    setTasks(tasksResponse);
                }
            });
        }
        if (!open && !value && searchPhrase) {
            setSearchPhrase('');
        }
    };

    const handleSelect = (_value: SelectValue): void => {
        const selectedTaskIds = (_value as string[]).map(Number); // Convert selected task IDs to numbers
        const selectedTaskNames = tasks.filter((t) => selectedTaskIds.includes(t.id)).map((t) => t.name);
        setSearchPhrase(selectedTaskNames.join(', ')); // Display selected task names
        onSelect(selectedTaskIds.length ? selectedTaskIds : null); // Update selected task IDs
    };


    useEffect(() => {
        if (value && value.length) {
            const missingTasks = value.filter((id) => !tasks.some((t) => t.id === id));
            if (missingTasks.length) {
                core.tasks.get({ id: missingTasks }).then((result: Task[]) => {
                    const newTasks = result.map((task) => ({
                        id: task.id,
                        name: task.name,
                    }));
                    setTasks([...tasks, ...newTasks]);
                    const selectedTaskNames = tasks.filter((t) => value.includes(t.id)).map((t) => t.name);
                    setSearchPhrase(selectedTaskNames.join(', '));
                });
            }
        } else {
            setSearchPhrase('');
        }
    }, [value]);

    useEffect(() => {
        setTasks([]);
    }, [filter]);

    useEffect(() => {
        setSearchPhrase('');
        onSelect([]);
        handleSearch('');
    }, [projectId]);

    return (
        <Select
            mode="multiple"
            value={value?.map(String)}
            placeholder="Select tasks"
            onSearch={handleSearch}
            onChange={handleSelect}
            className="cvat-task-search-field"
            onDropdownVisibleChange={handleFocus}
            options={tasks.map((proj) => ({
                value: proj.id.toString(),
                label: proj.name,
            }))}
        />
    );
}
